import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Timer, Zap, ArrowRight, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { SUBJECT_LABELS, subjectAccent, subjectsForPref } from "@/lib/pyqMeta";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export default function TopicPractice() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowedSubjects = subjectsForPref(user?.examPreference || "JEE");
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingSub, setLoadingSub] = useState(true);
  const [loadingCh, setLoadingCh] = useState(false);

  useEffect(() => {
    api.get("/pyq/meta")
      .then((r) => setSubjects(Object.keys(r.data.chaptersBySubject || {}).filter((s) => allowedSubjects.includes(s)).sort()))
      .finally(() => setLoadingSub(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickSubject = (s) => {
    setSubject(s);
    setSelected([]);
    setChapters([]);
    setLoadingCh(true);
    api.get("/pyq/chapters", { params: { subject: s } })
      .then((r) => setChapters(r.data))
      .finally(() => setLoadingCh(false));
  };

  const toggleChapter = (ch) =>
    setSelected((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]));

  const totalQs = chapters.filter((c) => selected.includes(c.chapter)).reduce((a, c) => a + c.count, 0);

  const start = (mode) => {
    if (!subject || selected.length === 0) return;
    const p = new URLSearchParams({ subject, chapter: selected.join(","), mode });
    navigate(`/student/session?${p.toString()}`);
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Practice by Topic</h1>
        <p className="mt-2 text-muted-foreground">Pick a subject and chapter, then start a focused practice session.</p>
      </div>

      {/* Step 1: Subject */}
      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">1 · Choose a subject</h2>
        {loadingSub ? (
          <div className="flex gap-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-32 rounded-full" />)}</div>
        ) : subjects.length === 0 ? (
          <p className="text-sm text-muted-foreground">No questions available yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="subject-chips">
            {subjects.map((s) => (
              <button
                key={s}
                data-testid={`subject-chip-${s}`}
                onClick={() => pickSubject(s)}
                className={cn(
                  "rounded-full border px-5 py-2 text-sm font-semibold transition-colors",
                  subject === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary hover:border-primary/40"
                )}
              >
                <span className={subject === s ? "" : subjectAccent(s)}>{SUBJECT_LABELS[s] || s}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Step 2: Chapter */}
      {subject && (
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">2 · Choose chapters <span className="normal-case text-muted-foreground/70">(select one or more)</span></h2>
          {loadingCh ? (
            <div className="grid gap-2 sm:grid-cols-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
          ) : chapters.length === 0 ? (
            <p className="text-sm text-muted-foreground">No chapters with questions for this subject yet.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2" data-testid="chapter-list">
              {chapters.map((c) => {
                const on = selected.includes(c.chapter);
                return (
                  <button
                    key={c.chapter}
                    data-testid={`chapter-item-${c.chapter}`}
                    aria-pressed={on}
                    onClick={() => toggleChapter(c.chapter)}
                    className={cn(
                      "flex items-center justify-between rounded-xl border p-3.5 text-left text-sm transition-colors",
                      on ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
                    )}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      <span className={cn("flex h-4 w-4 items-center justify-center rounded border", on ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40")}>
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <Layers className="h-4 w-4 text-primary" /> {c.chapter}
                    </span>
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-muted-foreground">{c.count} Q{c.count === 1 ? "" : "s"}</span>
                  </button>
                );
              })}
            </div>
          )}
        </motion.section>
      )}

      {/* Step 3: Mode + start */}
      {selected.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="start-section">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">3 · Start your session</h2>
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="mb-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{SUBJECT_LABELS[subject] || subject}</span> · {selected.length} chapter{selected.length === 1 ? "" : "s"} · {totalQs} question{totalQs === 1 ? "" : "s"}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => start("UNTIMED")} data-testid="start-untimed-btn" className="rounded-full px-6">
                <Zap className="mr-1.5 h-4 w-4" /> Untimed practice <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button onClick={() => start("TIMED")} variant="outline" data-testid="start-timed-btn" className="rounded-full px-6">
                <Timer className="mr-1.5 h-4 w-4" /> Timed session
              </Button>
            </div>
          </div>
        </motion.section>
      )}
    </div>
  );
}
